if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.mainFormSelector = this.getAttribute('data-main-form-selector');
        this.variantText = this.querySelector('.sticky-add-to-cart__variant-text');
        this.addToCartButton = this.querySelector('.sticky-add-to-cart__button');
        this.observer = null;
        this.mainForm = null;
        this.mainAddToCartButton = null;
        this.proPackageCheckbox = null;
        this.eWheelsCheckbox = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        document.removeEventListener('variant:change', this.handleVariantChange);
        if (this.addToCartButton) {
          this.addToCartButton.removeEventListener('click', this.handleAddToCart);
        }
        if (this.proPackageCheckbox) {
          this.proPackageCheckbox.removeEventListener('change', this.handleProPackageChange);
        }
        if (this.eWheelsCheckbox) {
          this.eWheelsCheckbox.removeEventListener('change', this.handleProPackageChange);
        }
      }

      init() {
        // Find main form and add to cart button
        this.findMainElements();
        
        // Find pro package checkboxes
        this.findProPackageElements();
        
        // Set up intersection observer
        this.setupIntersectionObserver();
        
        // Listen for variant changes
        this.handleVariantChange = this.handleVariantChange.bind(this);
        document.addEventListener('variant:change', this.handleVariantChange);
        
        // Listen for pro package changes
        this.handleProPackageChange = this.handleProPackageChange.bind(this);
        if (this.proPackageCheckbox) {
          this.proPackageCheckbox.addEventListener('change', this.handleProPackageChange);
        }
        if (this.eWheelsCheckbox) {
          this.eWheelsCheckbox.addEventListener('change', this.handleProPackageChange);
        }
        
        // Handle sticky add to cart button clicks
        this.handleAddToCart = this.handleAddToCart.bind(this);
        if (this.addToCartButton) {
          this.addToCartButton.addEventListener('click', this.handleAddToCart);
        }

        // Initial variant text update
        this.updateVariantText();

        const mainAtc = document.querySelector('#add-to-cart-btn');
        const mainAtcOos =
          mainAtc &&
          (typeof window.isMainAtcOutOfStock === 'function'
            ? window.isMainAtcOutOfStock(mainAtc)
            : mainAtc.disabled && !mainAtc.classList.contains('loading'));
        if (mainAtcOos) {
          if (this.addToCartButton.querySelector('span')) {
            this.addToCartButton.querySelector('span').textContent = 'Out of Stock';
          }
        }
      }

      findMainElements() {
        this.mainForm = document.querySelector(this.mainFormSelector);
        if (this.mainForm) {
          // Look for add to cart button in the main form
          this.mainAddToCartButton = this.mainForm.querySelector('button[type="submit"], input[type="submit"], .add-to-cart-button');
        }
      }

      findProPackageElements() {
        this.proPackageCheckbox = document.querySelector('#add-pro-package');
        this.eWheelsCheckbox = document.querySelector('#add-e-wheels-package');
      }

      setupIntersectionObserver() {
        if (!this.mainAddToCartButton) return;

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Main add to cart is visible, hide sticky
              this.classList.remove('sticky-add-to-cart--visible');
            } else {
              // Main add to cart is not visible, show sticky
              this.classList.add('sticky-add-to-cart--visible');
            }
          });
        }, {
          rootMargin: '-10px 0px',
          threshold: 0
        });

        this.observer.observe(this.mainAddToCartButton);
      }

      handleVariantChange(event) {
        if (event.detail && event.detail.variant) {
          const variantId = event.detail.variant.id;
          const variantTitle = event.detail.variant.title;
          
          // Update button variant ID
          if (this.addToCartButton) {
            this.addToCartButton.setAttribute('data-variant-id', variantId);
            
            // Update button availability state
            const variant = window.productVariants?.find(v => String(v.id) === String(variantId));
            if (variant) {
              this.updateButtonAvailability(variant.available);
            }
          }

          // Update variant text
          this.updateVariantText(variantTitle);
        }
      }

      updateButtonAvailability(available) {
        if (!this.addToCartButton) return;
        
        const btnText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');

        if (available) {
          // In stock
          this.addToCartButton.removeAttribute('disabled');
          this.addToCartButton.removeAttribute('aria-disabled');
          if (btnText) {
            btnText.textContent = 'Add to Cart';
          }
        } else {
          // Out of stock
          this.addToCartButton.setAttribute('disabled', '');
          this.addToCartButton.removeAttribute('aria-disabled');
          if (btnText) {
            btnText.textContent = 'Sold Out';
          }
        }
      }

      handleProPackageChange() {
        // Update variant text when pro packages change
        this.updateVariantText();
      }

      updateVariantText(selectedColor = null) {
        if (!this.variantText) return;

        // Get current seats information
        let seatsText = '2-Seater'; // Default
        
        // Try to get seats from product metafields or calculate from variants
        const currentProductSeats = document.querySelector('.dynamic-variant-heading')?.textContent?.match(/(\d+)-Seater/);
        if (currentProductSeats) {
          seatsText = currentProductSeats[0];
        }

        // Get color variant
        let colorText = selectedColor;
        if (!colorText) {
          // Try to get from selected color variant
          const selectedColorInput = document.querySelector('color-variant-selection input[type="radio"]:checked');
          if (selectedColorInput) {
            colorText = selectedColorInput.getAttribute('data-variant-title');
          }
        }

        // Combine texts
        let combinedText = seatsText;
        if (colorText) {
          combinedText += `, ${colorText}`;
        }

        // Check for pro package selections
        const proPackageSelected = this.proPackageCheckbox && this.proPackageCheckbox.checked;
        const eWheelsSelected = this.eWheelsCheckbox && this.eWheelsCheckbox.checked;

        if (proPackageSelected) {
          combinedText += ', Luxe Pro upgrade';
        }

        if (eWheelsSelected) {
          combinedText += ', E-wheels';
        }

        this.variantText.textContent = combinedText;
      }

      handleAddToCart() {
        if (!this.addToCartButton) return;

        if (!this.mainForm) return;

        const variantId = this.addToCartButton.getAttribute('data-variant-id');
        if (!variantId) return;

        // Update main form's variant ID
        const mainVariantInput = this.mainForm.querySelector('input[name="id"]');
        if (mainVariantInput) {
          mainVariantInput.value = variantId;
        }

        // Submit the main form
        const submitButton = this.mainForm.querySelector('button[type="submit"], input[type="submit"]');
        if (submitButton) {
          submitButton.click();
        }
      }
    }
  );
}
